// React and MUI imports for hooks, layout, and UI
import React, { useEffect } from 'react';
import { Box, Typography, Container, useMediaQuery, useTheme, CssBaseline } from '@mui/material';
import Header from '../../commons/Header'; // App header
import Footer from '../../commons/Footer'; // App footer
import HDButton from '../../hd-ui-kit/HDButton'; // Custom button
import { useNavigate } from 'react-router'; // React Router navigation
import Api from '../../Api'; // API utility for backend calls
import { reactGAEvents, titleStyle, styledText, styledSignInBtn, styledBoxMainWrapper, styledDividerLeft } from '../../commons/commonFunctions'; // Common style and analytics helpers

/**
 * ForgetPasswordActivate
 * Page shown after a user requests a password reset, instructing them to check their email for the reset link.
 * Includes a button to return to the sign-in screen.
 */
const ForgetPasswordActivate = () => {
  // Access the current MUI theme object
  const theme = useTheme();
  // Determine if the screen is mobile size for responsive layout
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // React Router navigation function
  const navigate = useNavigate();
  // Current theme mode ('light' or 'dark')
  const themeMode = theme.palette.mode;

  // Style for the main vertical flex box containing the title and instructions
  const MainStyledBox = {
    display: 'flex',
    flexDirection: 'column',
  };

  // Style for the wrapper around the sign-in button
  const StyledForgetWrapper = {
    textAlign: 'left',
    marginTop: '20px',
  };

  // On mount, redirect to home if already authorized (user is logged in)
  useEffect(() => {
    let isAuth = Api.isAuthorized();
    if (isAuth) navigate('/home');
  }, []);

  /**
   * Handler for the 'Return to sign-in screen' button.
   * Triggers a Google Analytics event and navigates to the login page.
   */
  const handleSingIn = () => {
    reactGAEvents('reset-password-activated', 'return-to-sign-in-screen');
    navigate('/login');
  };

  return (
    <>
      {/* CssBaseline normalizes browser styles for consistency */}
      <CssBaseline />
      {/* App header (logo, nav, etc.) */}
      <Header />
      {/* Main background section with theme-based color/gradient */}
      <Box sx={styledBoxMainWrapper(isMobile, themeMode)}>
        <Container maxWidth="lg">
          {/* Optional left divider for layout aesthetics */}
          <Box sx={styledDividerLeft(isMobile, theme)}>
            <Box>
              {/* Main content: title and instructions for password reset */}
              <Box sx={MainStyledBox}>
                {/* Title message for the user */}
                <Typography variant="h2" sx={titleStyle(isMobile, themeMode)}>
                  A password reset link was sent
                </Typography>
                {/* Instructional text for password reset */}
                <Typography variant="h2" sx={styledText(themeMode)}>
                  You have requested a password reset. Please check your inbox for an email from Heidrick and follow the instructions to reset it.
                </Typography>
              </Box>
              {/* Button to return to sign-in */}
              <Box sx={StyledForgetWrapper}>
                <Box>
                  <Box>
                    <HDButton sx={styledSignInBtn(themeMode)} variant={'text'} text={'Return to sign-in screen'} onClick={() => handleSingIn()} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* App footer (copyright, links, etc.) */}
      <Footer />
    </>
  );
};

export default ForgetPasswordActivate;
